import React, { useState, useEffect, useMemo } from 'react';
import {
  Upload,
  Image as ImageIcon,
  Search,
  Trash2,
  Edit,
  Download,
  FolderPlus,
  X,
  Grid3x3,
  List,
  Loader2,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  createFolder,
  getAllMedia,
  getFolderList,
  uploadMedia,
  deleteMedia,
} from '../services/mediaLibraryApi';

const MEDIA_BASE_URL = 'https://prop-api.8100081.xyz';

export const MediaLibraryPage = () => {
  const [mediaItems, setMediaItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [deleteMediaItem, setDeleteMediaItem] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadFolder, setUploadFolder] = useState('');
  const [uploadAltText, setUploadAltText] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');

  const [editingMedia, setEditingMedia] = useState(null);
  const [editAltText, setEditAltText] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // ==================== HELPERS ====================
  const getName = (item) =>
    item?.original_filename || item?.stored_filename || 'Untitled';

  const getUrl = (item) => {
    if (!item) return '';
    if (item.url || item.file_url) return item.url || item.file_url;

    const folder = item.folder_name;
    const file = item.stored_filename;

    if (folder && file) {
      return `${MEDIA_BASE_URL}/media-library/public/file/${folder}/${file}`;
    }
    return '';
  };

  const getSize = (item) => {
    if (item?.file_size === null || item?.file_size === undefined) return '-';
    const bytes = Number(item.file_size);
    if (isNaN(bytes)) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getAlt = (item) => item?.alt_text || '';
  const getFolder = (item) => item?.folder_name || selectedFolder;
  const getMimeType = (item) => item?.mime_type || '';
  const getCreatedAt = (item) => {
    if (!item?.created_at) return '-';
    try {
      return new Date(item.created_at).toLocaleDateString();
    } catch {
      return item.created_at;
    }
  };

  // ==================== FETCH FOLDERS ====================
  const fetchFolders = async () => {
    try {
      setFoldersLoading(true);
      const res = await getFolderList();
      const list = res?.data || res || [];
      const names = Array.isArray(list)
        ? list.map((f) => (typeof f === 'string' ? f : f.name)).filter(Boolean)
        : [];
      setFolders(names);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load folders');
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  };

  // ==================== FETCH MEDIA ====================
  const fetchMedia = async (folderName, folderList = folders) => {
    try {
      setLoading(true);

      // All folders → fetch each and merge
      if (!folderName || folderName === 'all') {
        if (!folderList.length) {
          setMediaItems([]);
          return;
        }

        const results = await Promise.all(
          folderList.map(async (folder) => {
            try {
              const res = await getAllMedia(folder);
              return res?.data || (Array.isArray(res) ? res : []);
            } catch {
              return [];
            }
          })
        );

        setMediaItems(results.flat());
        return;
      }

      // Single folder
      const res = await getAllMedia(folderName);
      const items = res?.data || (Array.isArray(res) ? res : []);
      setMediaItems(items);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load media');
      setMediaItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFolders();
  }, []);

  useEffect(() => {
    if (foldersLoading) return;

    if (selectedFolder === 'all') {
      if (folders.length > 0) {
        fetchMedia('all', folders);
      } else {
        setMediaItems([]);
      }
    } else if (selectedFolder) {
      fetchMedia(selectedFolder, folders);
    }
  }, [selectedFolder, folders, foldersLoading]);

  // ==================== FILTER ====================
  const filteredMedia = useMemo(() => {
    return mediaItems.filter((item) => {
      const name = item?.original_filename || item?.stored_filename || '';
      const alt = item?.alt_text || '';
      return (
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        alt.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [mediaItems, searchTerm]);

  // ==================== CREATE FOLDER ====================
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    try {
      await createFolder(newFolderName.trim());
      toast.success(`Folder "${newFolderName}" created`);
      setNewFolderOpen(false);
      setNewFolderName('');
      fetchFolders();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to create folder');
    }
  };

  // ==================== UPLOAD ====================
  const handleFileSelect = (e) => {
    setUploadFiles(Array.from(e.target.files || []));
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      toast.error('Please select at least one file');
      return;
    }
    if (!uploadFolder) {
      toast.error('Please select a folder');
      return;
    }

    setUploading(true);
    try {
      for (const file of uploadFiles) {
        await uploadMedia({
          file,
          folder_name: uploadFolder,
          alt_text: uploadAltText,
          description: uploadDescription,
        });
      }
      toast.success(`${uploadFiles.length} file(s) uploaded successfully`);
      setUploadDialogOpen(false);
      setUploadFiles([]);
      setUploadAltText('');
      setUploadDescription('');

      if (selectedFolder === 'all' || selectedFolder === uploadFolder) {
        fetchMedia(selectedFolder, folders);
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ==================== EDIT ====================
  const handleEdit = (media) => {
    setEditingMedia(media);
    setEditAltText(media?.alt_text || '');
    setEditTitle(media?.original_filename || '');
    setEditDescription(media?.description || '');
  };

  const handleSaveEdit = () => {
    setMediaItems((prev) =>
      prev.map((m) =>
        m.id === editingMedia.id
          ? {
              ...m,
              alt_text: editAltText,
              original_filename: editTitle,
              description: editDescription,
            }
          : m
      )
    );
    toast.success('Media updated locally (no update API yet)');
    setEditingMedia(null);
  };

  // ==================== DELETE ====================
  const handleDelete = async () => {
    if (!deleteMediaItem) return;

    try {
      await deleteMedia(deleteMediaItem.id);
      toast.success('Media deleted successfully');
      setDeleteMediaItem(null);
      setSelectedMedia(null);
      fetchMedia(selectedFolder, folders);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to delete');
    }
  };

  return (
    <div>
      <PageHeader
        title="Media Library"
        description="Manage images, logos, favicons, and other media files"
        breadcrumbs={['Admin', 'Media Library']}
      />

      {/* Toolbar */}
      <Card className="rounded-xl border-slate-200 shadow-sm mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by filename or alt text..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <Select
              value={selectedFolder}
              onValueChange={setSelectedFolder}
              disabled={foldersLoading}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select folder" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Folders</SelectItem>
                {folders.map((folder) => (
                  <SelectItem key={folder} value={folder}>
                    {folder}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex gap-1 border border-slate-200 rounded-md p-1">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="h-8 px-3"
              >
                <Grid3x3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="h-8 px-3"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>

            <Button onClick={() => setNewFolderOpen(true)} variant="outline">
              <FolderPlus className="h-4 w-4 mr-2" />
              New Folder
            </Button>

            <Button onClick={() => setUploadDialogOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Upload Files
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Media Grid/List */}
      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-slate-600">
              {loading
                ? 'Loading...'
                : `${filteredMedia.length} ${
                    filteredMedia.length === 1 ? 'item' : 'items'
                  }`}
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filteredMedia.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-2">No media files found</p>
              <p className="text-sm text-slate-500">Upload files to get started</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filteredMedia.map((media) => (
                <div
                  key={media.id}
                  className="group relative border-2 border-slate-200 rounded-lg overflow-hidden hover:border-blue-400 transition-all cursor-pointer"
                  onClick={() => setSelectedMedia(media)}
                >
                  <div className="aspect-square bg-slate-100 flex items-center justify-center p-2">
                    {getUrl(media) ? (
                      <img
                        src={getUrl(media)}
                        alt={getAlt(media)}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <ImageIcon className="h-12 w-12 text-slate-400" />
                    )}
                  </div>
                  <div className="p-2 bg-white">
                    <p className="text-xs font-medium text-slate-900 truncate">
                      {getName(media)}
                    </p>
                    <p className="text-xs text-slate-500">{getSize(media)}</p>
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="icon"
                      variant="secondary"
                      className="h-7 w-7 shadow-lg"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(media);
                      }}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMedia.map((media) => (
                <div
                  key={media.id}
                  className="flex items-center gap-4 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer"
                  onClick={() => setSelectedMedia(media)}
                >
                  <div className="h-12 w-12 bg-slate-100 rounded flex items-center justify-center flex-shrink-0 overflow-hidden p-1">
                    {getUrl(media) ? (
                      <img
                        src={getUrl(media)}
                        alt={getAlt(media)}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <ImageIcon className="h-6 w-6 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {getName(media)}
                    </p>
                    <p className="text-xs text-slate-500">{getSize(media)}</p>
                  </div>
                  <Badge variant="secondary">{getFolder(media)}</Badge>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(media);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Files</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Select Files</Label>
              <Input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                className="mt-2"
              />
              <p className="text-xs text-slate-500 mt-1">
                Supports: JPG, PNG, GIF, SVG, WebP
              </p>
              {uploadFiles.length > 0 && (
                <p className="text-sm text-slate-600 mt-2">
                  {uploadFiles.length} file(s) selected
                </p>
              )}
            </div>

            <div>
              <Label>Upload to Folder</Label>
              <Select value={uploadFolder} onValueChange={setUploadFolder}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select folder" />
                </SelectTrigger>
                <SelectContent>
                  {folders.map((folder) => (
                    <SelectItem key={folder} value={folder}>
                      {folder}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Alt Text (optional)</Label>
              <Input
                value={uploadAltText}
                onChange={(e) => setUploadAltText(e.target.value)}
                placeholder="Describe the image"
                className="mt-2"
              />
            </div>

            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="Additional notes"
                rows={2}
                className="mt-2"
              />
            </div>

            <Button className="w-full" onClick={handleUpload} disabled={uploading}>
              {uploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Upload {uploadFiles.length > 0 ? `(${uploadFiles.length})` : ''}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Folder Dialog */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Folder Name</Label>
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="e.g., Product Images"
                className="mt-2"
              />
            </div>
            <Button onClick={handleCreateFolder} className="w-full">
              Create Folder
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Media Dialog */}
      <Dialog open={!!editingMedia} onOpenChange={() => setEditingMedia(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Media</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex gap-4">
              <div className="w-48 h-48 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden p-2">
                {getUrl(editingMedia) ? (
                  <img
                    src={getUrl(editingMedia)}
                    alt=""
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <ImageIcon className="h-16 w-16 text-slate-400" />
                )}
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <Label>File Name</Label>
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="mt-2"
                  />
                </div>
                <div>
                  <p className="text-sm text-slate-600">
                    Size: {getSize(editingMedia)}
                  </p>
                  <p className="text-sm text-slate-600">
                    Type: {getMimeType(editingMedia)}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label>Alt Text (for SEO)</Label>
              <Input
                value={editAltText}
                onChange={(e) => setEditAltText(e.target.value)}
                placeholder="Describe the image for screen readers and SEO"
                className="mt-2"
              />
            </div>

            <div>
              <Label>Description (Optional)</Label>
              <Textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Additional notes about this media file"
                rows={3}
                className="mt-2"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSaveEdit} className="flex-1">
                Save Changes
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(getUrl(editingMedia));
                  toast.success('URL copied to clipboard');
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Copy URL
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setDeleteMediaItem(editingMedia);
                  setEditingMedia(null);
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media Details Side Panel */}
      {selectedMedia && !editingMedia && (
        <div className="fixed right-0 top-0 h-full w-96 bg-white border-l border-slate-200 shadow-2xl z-50 overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Media Details</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedMedia(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="aspect-square bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden p-4">
                {getUrl(selectedMedia) ? (
                  <img
                    src={getUrl(selectedMedia)}
                    alt={getAlt(selectedMedia)}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <ImageIcon className="h-24 w-24 text-slate-400" />
                )}
              </div>

              <div>
                <p className="text-sm font-medium text-slate-900">
                  {getName(selectedMedia)}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {getFolder(selectedMedia)}
                </p>
              </div>

              <div className="space-y-2 pt-4 border-t">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Size:</span>
                  <span className="font-medium">{getSize(selectedMedia)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Type:</span>
                  <span className="font-medium">{getMimeType(selectedMedia)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Uploaded:</span>
                  <span className="font-medium">{getCreatedAt(selectedMedia)}</span>
                </div>
                {getAlt(selectedMedia) && (
                  <div className="pt-2">
                    <p className="text-xs text-slate-600 mb-1">Alt Text:</p>
                    <p className="text-sm text-slate-900">{getAlt(selectedMedia)}</p>
                  </div>
                )}
                {selectedMedia?.description && (
                  <div className="pt-2">
                    <p className="text-xs text-slate-600 mb-1">Description:</p>
                    <p className="text-sm text-slate-900">
                      {selectedMedia.description}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 pt-4">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleEdit(selectedMedia)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Details
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    navigator.clipboard.writeText(getUrl(selectedMedia));
                    toast.success('URL copied to clipboard');
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Copy URL
                </Button>
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => setDeleteMediaItem(selectedMedia)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete File
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteMediaItem}
        onOpenChange={() => setDeleteMediaItem(null)}
        title="Delete Media File"
        description={`Are you sure you want to delete "${getName(
          deleteMediaItem
        )}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </div>
  );
};